import { NavLink, useNavigate } from "react-router-dom";
import { useFieldTracking } from "@/offline/useFieldTracking";
import {
  LayoutDashboard,
  Users,
  Database,
  CheckSquare,
  BarChart3,
  ChevronDown,
  ChevronRight,
  LogOut,
  Smartphone,
  KeyRound,
  Menu,
  MapPin,
  ScrollText,
  Package,
  Barcode,
  Tag,
  Gift,
  Building2,
  Store,
  Route as RouteIcon,
  Truck,
  Clock,
  Calendar,
  ClipboardList,
  CalendarDays,
  Users2,
  MapPinned,
  ShoppingCart,
  FileText,
  Award,
  Wallet,
  Settings,
  ShieldCheck,
  Star,
  Search,
  X,
  MessagesSquare,
  ListChecks,
  Megaphone,
  Mail,
  Palette, History,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { TulsiLogo } from "@/lib/brand";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import SyncPill from "@/offline/SyncPill";
import NotificationBell from "@/components/NotificationBell";
import BroadcastPopup from "@/components/BroadcastPopup";
import LocationRequiredPopup from "@/components/LocationRequiredPopup";
import GlobalSearch from "@/components/GlobalSearch";
import { useFavorites } from "@/lib/favorites";

// Phase 26A · Nav items are tagged with a `group` so the sidebar can render
// them into collapsible groups. Dashboard is intentionally ungrouped (pinned
// at the top). Six groups per user spec:
//   MASTERS | FIELD OPS | ORDERS_INVENTORY | MONEY | REPORTS | SETTINGS
// ASM / ZSM in Browser view get exactly what the field app's More menu and
// Reports hub give them — not the whole admin sidebar their permissions would
// otherwise light up (Last Login, Mobile Health, scheme/primary reports, …).
const LIMITED_MANAGER_ROLES = ["ASM", "ZSM"];
const LIMITED_MANAGER_NAV = new Set([
  "/",
  "/admin/pjp-requests", "/admin/user-map", "/attendance-calendar", "/my-targets",
  "/admin/orders",
  "/admin/tada-approval", "/admin/primary-sale-return-approval",
  // field-app screens that had no browser sidebar entry (added below)
  "/field/my-day-summary", "/field/my-activity-submissions", "/primary-order",
  "/manager/retailer-list", "/distributor-stock", "/manager/team-claims",
  // same ten reports as the field Reports hub (pages/manager/ReportsHub.jsx)
  "/reports/order", "/reports/visit", "/reports/working", "/reports/new-call",
  "/reports/sales-return", "/reports/day-summary",
  "/reports/target-achievement", "/reports/inventory", "/sales-report",
  "/queries",
  "/messages",
]);

// Entries that exist only for the limited manager view (other roles use the
// admin pages for these, or don't need them).
const LIMITED_MANAGER_ONLY = new Set([
  "/field/my-day-summary", "/field/my-activity-submissions", "/primary-order",
  "/manager/retailer-list", "/distributor-stock", "/manager/team-claims",
  "/queries",
]);

// SALES_COORDINATOR only handles distributor queries — many items below are
// show:true for everyone, so this role gets an explicit allow-list instead.
const COORDINATOR_NAV = new Set(["/admin/queries", "/admin/query-reasons", "/reports/distributor-queries", "/reports/distributor-query-conversations", "/admin/distributor-targets", "/admin/broadcasts", "/admin/outlet-colors", "/messages"]);

function buildNav(user, hasPermission, hasAnyPermission, hasWorkflowPerm) {
  const items = [
    // ---------- Dashboard (ungrouped, pinned) ----------
    { to: "/", label: "Dashboard", icon: LayoutDashboard, show: true, exact: true, testid: "nav-dashboard", group: null },
    // Everyone's own inbox of office broadcast messages (re-read after the popup's OK)
    { to: "/messages", label: "My messages", icon: Mail, show: true, testid: "nav-my-messages", group: null, keywords: ["message", "broadcast", "announcement", "inbox"] },

    // ---------- MASTERS ----------
    { to: "/admin/users",                 label: "Users",                icon: Users,       show: hasPermission("users.manage"),   testid: "nav-users",                 group: "MASTERS" },
    { to: "/admin/lookups",               label: "Lookups",              icon: Database,    show: hasPermission("lookups.manage"), testid: "nav-lookups",               group: "MASTERS" },
    { to: "/admin/geography",             label: "Geography",            icon: MapPin,      show: hasPermission("masters.manage"), testid: "nav-geography",             group: "MASTERS" },
    { to: "/admin/products",              label: "Products",             icon: Package,     show: hasPermission("masters.manage"), testid: "nav-products",              group: "MASTERS" },
    { to: "/admin/skus",                  label: "SKUs",                 icon: Barcode,     show: hasPermission("masters.manage"), testid: "nav-skus",                  group: "MASTERS" },
    { to: "/admin/pricing",               label: "Pricing",              icon: Tag,         show: hasPermission("masters.manage"), testid: "nav-pricing",               group: "MASTERS" },
    { to: "/admin/schemes",               label: "Schemes",              icon: Gift,        show: hasPermission("masters.manage"), testid: "nav-schemes",               group: "MASTERS" },
    { to: "/admin/distributors",          label: "Distributors",         icon: Building2,   show: hasPermission("masters.manage"), testid: "nav-distributors",          group: "MASTERS", keywords: ["distribution"] },
    { to: "/admin/distributor-groups",    label: "Distributor Groups",   icon: Building2,   show: hasPermission("masters.manage"), testid: "nav-distributor-groups",    group: "MASTERS", keywords: ["distribution"] },
    { to: "/admin/price-lists",           label: "Price Lists",          icon: Tag,         show: hasPermission("masters.manage"), testid: "nav-price-lists",           group: "MASTERS" },
    { to: "/admin/uoms",                  label: "UOMs",                 icon: Barcode,     show: hasPermission("masters.manage"), testid: "nav-uoms",                  group: "MASTERS" },
    { to: "/admin/retailers",             label: "Retailers",            icon: Store,       show: hasPermission("masters.manage"), testid: "nav-retailers",             group: "MASTERS" },
    { to: "/admin/outlet-transfer",       label: "Bulk Retailer Transfer", icon: RouteIcon, show: hasPermission("masters.manage"), testid: "nav-bulk-retailer-transfer", group: "MASTERS" },
    { to: "/admin/retailer-transfer",     label: "Bulk Transfer · Outlet Mode", icon: RouteIcon, show: hasPermission("masters.manage"), testid: "nav-retailer-transfer-grid", group: "MASTERS" },
    { to: "/admin/target-bulk-import",    label: "Bulk Target Upload",   icon: RouteIcon, show: hasPermission("masters.manage"), testid: "nav-target-bulk-import",    group: "MASTERS" },
    { to: "/admin/inventory",             label: "Bulk Inventory Update", icon: RouteIcon, show: hasPermission("masters.manage"), testid: "nav-bulk-inventory-update", group: "MASTERS" },
    { to: "/admin/outlet-config",         label: "Outlet Configuration", icon: Store,       show: hasPermission("masters.manage"), testid: "nav-outlet-config",         group: "MASTERS" },
    { to: "/admin/beats",                 label: "Beats",                icon: RouteIcon,   show: hasPermission("masters.manage"), testid: "nav-beats",                 group: "MASTERS" },
    { to: "/admin/holidays",              label: "Holidays",             icon: Calendar,    show: hasPermission("masters.manage"), testid: "nav-holidays",              group: "MASTERS" },
    { to: "/admin/leave-policy",          label: "Leave Policy",         icon: ClipboardList, show: hasPermission("masters.manage"), testid: "nav-leave-policy",        group: "MASTERS" },
    { to: "/admin/attendance-settings",   label: "Attendance Settings",  icon: Clock,       show: hasPermission("masters.manage"), testid: "nav-attendance-settings",   group: "MASTERS" },
    { to: "/admin/attendance-policy",     label: "Attendance Policy",    icon: Clock,       show: ["SUPER_ADMIN","MANAGEMENT"].includes(user?.role_code), testid: "nav-attendance-policy", group: "MASTERS" },
    { to: "/admin/attendance-windows",    label: "Attendance Windows",   icon: Clock,       show: hasPermission("masters.manage"), testid: "nav-attendance-windows",    group: "MASTERS" },
    { to: "/admin/visit-settings",        label: "Visit Settings",       icon: MapPin,      show: hasPermission("masters.manage"), testid: "nav-visit-settings",        group: "MASTERS" },
    { to: "/admin/outlet-colors",         label: "Outlet Colour Coding", icon: Palette,     show: ["SUPER_ADMIN","MANAGEMENT","SALES_COORDINATOR"].includes(user?.role_code), testid: "nav-outlet-colors", group: "MASTERS", keywords: ["color", "colour", "outlet", "legend"] },
    { to: "/admin/import-past-orders",    label: "Import Past Orders",   icon: History,     show: user?.role_code === "SUPER_ADMIN", testid: "nav-import-past-orders", group: "MASTERS", keywords: ["old app", "history", "orders", "upload", "import"] },
    { to: "/admin/da-rates",              label: "DA Rates",             icon: Wallet,      show: hasPermission("masters.manage"), testid: "nav-da-rates",              group: "MASTERS" },
    { to: "/admin/travel-rates",          label: "Travel Rates",         icon: RouteIcon,   show: hasPermission("masters.manage"), testid: "nav-travel-rates",          group: "MASTERS" },
    { to: "/admin/tada-config",           label: "TA/DA Settings",       icon: Clock,       show: hasPermission("masters.manage"), testid: "nav-tada-config",           group: "MASTERS" },
    { to: "/admin/travel-policies",       label: "Travel Policies",      icon: ShieldCheck, show: hasPermission("masters.manage"), testid: "nav-travel-policies",       group: "MASTERS" },
    { to: "/admin/primary-order-config",  label: "Primary Order Config", icon: Truck,       show: hasPermission("masters.manage"), testid: "nav-primary-order-config",  group: "MASTERS" },
    { to: "/admin/forms",                 label: "Activity Forms",       icon: FileText,    show: hasPermission("forms.manage"),   testid: "nav-forms",                 group: "MASTERS" },
    { to: "/admin/query-reasons",         label: "Query Reasons",        icon: ListChecks,  show: ["SUPER_ADMIN","SALES_COORDINATOR"].includes(user?.role_code), testid: "nav-query-reasons", group: "MASTERS" },

    // ---------- FIELD OPS ----------
    { to: "/admin/pjp",                   label: "PJP Admin Calendar",   icon: CalendarDays,   show: hasPermission("pjp.manage"),               testid: "nav-pjp-admin",              group: "FIELD_OPS" },
    { to: "/admin/pjp-requests",          label: "PJP Change Requests",  icon: ClipboardList,  show: !["SE","DISTRIBUTOR"].includes(user?.role_code), testid: "nav-pjp-requests",       group: "FIELD_OPS" },
    { to: "/live-attendance",             label: "Live Attendance",      icon: Users2,         show: hasPermission("reports.attendance"),       testid: "nav-live-attendance",        group: "FIELD_OPS" },
    { to: "/manager/geo-tag-requests",    label: "Geo-tag Approvals",    icon: MapPinned,      show: hasPermission("geo_tag.approve"),          testid: "nav-geo-tag",                group: "FIELD_OPS" },
    { to: "/manager/activity-submissions", label: "Verify Activities",   icon: Award,          show: hasPermission("activities.verify"),        testid: "nav-verify-activities",      group: "FIELD_OPS" },
    { to: "/admin/user-map",              label: "Location Map",         icon: MapPin,         show: !["SE","DISTRIBUTOR"].includes(user?.role_code), testid: "nav-user-map",         group: "FIELD_OPS" },
    { to: "/admin/outlet-density-map",    label: "Outlet Density Map",   icon: MapPinned,      show: !["SE","DISTRIBUTOR"].includes(user?.role_code), testid: "nav-outlet-density-map", group: "FIELD_OPS" },
    { to: "/leave-approvals",             label: "Leave Approvals",      icon: CheckSquare,    show: hasPermission("leave.approve"),            testid: "nav-leave-approvals",        group: "FIELD_OPS" },

    // ---------- ORDERS & INVENTORY ----------
    { to: "/orders",                      label: "Orders",               icon: ShoppingCart, show: (hasPermission("orders.view") || hasPermission("orders.create")) && hasWorkflowPerm("ORDER"), testid: "nav-orders", group: "ORDERS_INVENTORY" },
    { to: "/admin/orders",                label: "Order Grid (ERP)",     icon: ShoppingCart, show: hasPermission("orders.approve"),                                    testid: "nav-admin-order-grid", group: "ORDERS_INVENTORY" },
    { to: "/approvals/inbox",             label: "Approvals Inbox",      icon: CheckSquare, show: true,                                             testid: "nav-approvals-inbox",  group: "ORDERS_INVENTORY" },
    { to: "/admin/approval-configs",      label: "Approval Configs",     icon: CheckSquare, show: hasAnyPermission(["approvals.manage"]),           testid: "nav-approval-configs", group: "ORDERS_INVENTORY" },
    { to: "/admin/queries",               label: "Distributor Queries",  icon: MessagesSquare, show: ["SUPER_ADMIN","SALES_COORDINATOR","MANAGEMENT"].includes(user?.role_code), testid: "nav-distributor-queries", group: "ORDERS_INVENTORY" },

    // ---------- MONEY ----------
    { to: "/admin/tada-approval",         label: "TA/DA Approval",       icon: Wallet,     show: !["SE","DISTRIBUTOR"].includes(user?.role_code),                      testid: "nav-tada-approval", group: "MONEY" },
    { to: "/admin/distributor-targets",    label: "Distributor Targets",  icon: BarChart3,   show: ["SUPER_ADMIN","MANAGEMENT","SALES_COORDINATOR"].includes(user?.role_code), testid: "nav-distributor-targets", group: "MONEY" },
    { to: "/admin/primary-sale-return-approval", label: "Primary SR Approval", icon: Wallet, show: ["ASM","ZSM","SUPER_ADMIN","MANAGEMENT"].includes(user?.role_code), testid: "nav-primary-sr-approval", group: "MONEY" },
    { to: "/admin/claims/grid",           label: "Claim Grid (ERP)",     icon: Wallet,     show: hasAnyPermission(["claims.approve"]),                                  testid: "nav-admin-claim-grid", group: "MONEY" },
    { to: "/manager/money-inbox",         label: "Money Inbox",          icon: CheckSquare, show: hasAnyPermission(["claims.approve","payments.verify"]),              testid: "nav-money-inbox",   group: "MONEY" },
    { to: "/targets",                     label: "Targets",              icon: BarChart3,   show: hasAnyPermission(["targets.set","targets.view"]),                    testid: "nav-targets",       group: "MONEY" },

    // ---------- REPORTS ----------
    { to: "/admin/inventory-dashboard",     label: "Inventory Dashboard",   icon: BarChart3, show: hasPermission("reports.view"),                                    testid: "nav-inventory-dashboard",       group: "REPORTS" },
    { to: "/reports/beat-coverage",         label: "Beat Coverage",         icon: BarChart3, show: hasPermission("reports.visits") || hasPermission("reports.view"), testid: "nav-report-beat-coverage",       group: "REPORTS" },
    { to: "/reports/missed-visits",         label: "Missed Visits",         icon: BarChart3, show: hasPermission("reports.visits") || hasPermission("reports.view"), testid: "nav-report-missed-visits",       group: "REPORTS" },
    { to: "/reports/daily-visit-summary",   label: "Daily Visits",          icon: BarChart3, show: hasPermission("reports.visits") || hasPermission("reports.view"), testid: "nav-report-daily-visits",        group: "REPORTS" },
    { to: "/reports/attendance",            label: "Attendance Report",     icon: BarChart3, show: hasPermission("reports.attendance"),                              testid: "nav-report-attendance",          group: "REPORTS" },
    { to: "/reports/last-login",            label: "Last Login",            icon: BarChart3, show: !["SE","DISTRIBUTOR"].includes(user?.role_code),                  testid: "nav-report-last-login",          group: "REPORTS" },
    { to: "/reports/mobile-health",         label: "Mobile Health",         icon: BarChart3, show: !["SE","DISTRIBUTOR"].includes(user?.role_code),                  testid: "nav-report-mobile-health",       group: "REPORTS" },
    { to: "/reports/inventory",             label: "Inventory",             icon: BarChart3, show: !["SE"].includes(user?.role_code),                                testid: "nav-report-inventory",           group: "REPORTS" },
    { to: "/reports/order",                 label: "Order Report",          icon: BarChart3, show: true,                                                             testid: "nav-report-order",               group: "REPORTS" },
    { to: "/reports/scheme",                label: "Scheme Report",         icon: BarChart3, show: true,                                                             testid: "nav-report-scheme",              group: "REPORTS" },
    { to: "/reports/primary-schemes",       label: "Primary Scheme Report", icon: BarChart3, show: !["SE"].includes(user?.role_code),                                testid: "nav-report-primary-schemes",     group: "REPORTS" },
    { to: "/reports/visit",                 label: "Visit Report",          icon: BarChart3, show: !["DISTRIBUTOR"].includes(user?.role_code),                       testid: "nav-report-visit-24b",           group: "REPORTS" },
    { to: "/reports/working",               label: "Working Report",        icon: BarChart3, show: !["DISTRIBUTOR"].includes(user?.role_code),                       testid: "nav-report-working",             group: "REPORTS" },
    { to: "/reports/new-call",              label: "New Call Report",       icon: BarChart3, show: !["DISTRIBUTOR"].includes(user?.role_code),                       testid: "nav-report-new-call",            group: "REPORTS" },
    { to: "/reports/sales-return",          label: "Sales Return",          icon: BarChart3, show: true,                                                             testid: "nav-report-sales-return",        group: "REPORTS" },
    { to: "/reports/day-summary",           label: "Day Summary",           icon: BarChart3, show: !["DISTRIBUTOR"].includes(user?.role_code),                       testid: "nav-report-day-summary",         group: "REPORTS" },
    { to: "/reports/device-access",         label: "Device Access Report",  icon: BarChart3, show: ["SUPER_ADMIN","MANAGEMENT"].includes(user?.role_code),             testid: "nav-report-device-access",       group: "REPORTS" },
    { to: "/reports/user-km",               label: "User Km Report",        icon: BarChart3, show: !["SE","DISTRIBUTOR"].includes(user?.role_code),                   testid: "nav-report-user-km",             group: "REPORTS" },
    { to: "/reports/beat-report",            label: "Beat Report",           icon: BarChart3, show: ["SUPER_ADMIN","MANAGEMENT","MIS","SALES_HEAD","RSM"].includes(user?.role_code), testid: "nav-report-beat-report", group: "REPORTS" },
    { to: "/reports/inactive-outlets",       label: "Inactive Outlets",      icon: BarChart3, show: ["SUPER_ADMIN","MANAGEMENT","MIS","SALES_HEAD","RSM"].includes(user?.role_code), testid: "nav-report-inactive-outlets", group: "REPORTS" },
    { to: "/reports/claims",                label: "Claim Report",          icon: BarChart3, show: !["DISTRIBUTOR"].includes(user?.role_code),                       testid: "nav-report-claims",              group: "REPORTS" },
    { to: "/reports/primary-orders",        label: "Primary Orders",        icon: BarChart3, show: !["SE"].includes(user?.role_code),                                testid: "nav-report-primary-orders",      group: "REPORTS" },
    { to: "/reports/primary-sales-returns", label: "Primary Sales Return",  icon: BarChart3, show: !["SE"].includes(user?.role_code),                                testid: "nav-report-primary-sr",          group: "REPORTS" },
    { to: "/reports/sales-and-returns",     label: "Sales + Returns",       icon: BarChart3, show: !["SE"].includes(user?.role_code),                                testid: "nav-report-sales-and-returns",   group: "REPORTS" },
    { to: "/reports/activity-submissions",  label: "Activity Report",       icon: BarChart3, show: hasAnyPermission(["reports.activity", "reports.activity.own", "reports.view"]), testid: "nav-report-activity", group: "REPORTS" },
    { to: "/reports/display-compliance",    label: "Display Compliance",    icon: BarChart3, show: hasAnyPermission(["reports.activity", "reports.view"]),           testid: "nav-report-display-compliance",  group: "REPORTS" },
    { to: "/reports/money",                 label: "Money Reports",         icon: BarChart3, show: hasAnyPermission(["reports.payments","reports.claims","reports.expenses","reports.view"]), testid: "nav-money-reports",         group: "REPORTS" },
    { to: "/reports/target-achievement",    label: "Target Achievement",    icon: BarChart3, show: hasAnyPermission(["reports.targets","reports.view","targets.view"]),               testid: "nav-report-targets",             group: "REPORTS" },
    { to: "/reports/distributor-queries",   label: "Distributor Query Report", icon: BarChart3, show: ["SUPER_ADMIN","SALES_COORDINATOR","MANAGEMENT"].includes(user?.role_code), testid: "nav-report-distributor-queries", group: "REPORTS" },
    { to: "/reports/distributor-query-conversations",   label: "Query Conversation Report", icon: BarChart3, show: ["SUPER_ADMIN","SALES_COORDINATOR","MANAGEMENT"].includes(user?.role_code), testid: "nav-report-distributor-query-conversations", group: "REPORTS" },
    { to: "/reports",                       label: "All Reports",           icon: BarChart3, show: hasPermission("reports.view"),                                    testid: "nav-reports",                    group: "REPORTS" },

    // ---------- SETTINGS ----------
    { to: "/admin/audit",         label: "Audit log",     icon: ScrollText, show: ["SUPER_ADMIN","MIS","MANAGEMENT"].includes(user?.role_code), testid: "nav-audit",        group: "SETTINGS" },
    { to: "/admin/device-change-requests", label: "Device change requests", icon: Settings, show: ["SUPER_ADMIN","MANAGEMENT"].includes(user?.role_code), testid: "nav-device-change-requests", group: "SETTINGS" },
    { to: "/admin/geo-exceptions", label: "Geo-exception approvals", icon: Settings, show: ["SUPER_ADMIN","MANAGEMENT","SALES_HEAD","ZSM","RSM","ASM"].includes(user?.role_code), testid: "nav-geo-exceptions", group: "SETTINGS" },
    { to: "/admin/focus-skus", label: "Focus SKUs", icon: Settings, show: ["SUPER_ADMIN","MANAGEMENT"].includes(user?.role_code), testid: "nav-focus-skus", group: "SETTINGS" },
    { to: "/admin/system-setup",  label: "System Setup",  icon: Settings,   show: user?.role_code === "SUPER_ADMIN",                             testid: "nav-system-setup", group: "SETTINGS" },
    { to: "/admin/email-settings", label: "Email Settings", icon: Mail, show: hasPermission("email.manage"), testid: "nav-email-settings", group: "SETTINGS", keywords: ["email", "smtp", "mail", "order email"] },
    { to: "/admin/broadcasts",    label: "Broadcast Messages", icon: Megaphone, show: ["SUPER_ADMIN","MANAGEMENT","SALES_COORDINATOR"].includes(user?.role_code), testid: "nav-broadcasts", group: "SETTINGS", keywords: ["message", "notification", "announcement", "broadcast"] },

    // ---------- own-month attendance calendar (everyone who punches attendance) ----------
    { to: "/attendance-calendar",         label: "My attendance",         icon: Calendar, show: !["DISTRIBUTOR","SALES_COORDINATOR"].includes(user?.role_code), testid: "nav-attendance-calendar", group: "FIELD_OPS" },
    { to: "/my-targets",                   label: "My targets (kg)",      icon: BarChart3, show: !["DISTRIBUTOR","SALES_COORDINATOR"].includes(user?.role_code), testid: "nav-my-targets", group: "FIELD_OPS" },
    { to: "/sales-report",                 label: "Sales report",         icon: BarChart3, show: ["SE","ASM","ZSM","RSM","SALES_HEAD"].includes(user?.role_code), testid: "nav-sales-report", group: "FIELD_OPS" },

    // ---------- ASM / ZSM only: the field app's own screens ----------
    { to: "/field/my-day-summary",        label: "My day summary",        icon: ClipboardList, show: true, testid: "nav-my-day-summary",        group: "FIELD_OPS" },
    { to: "/field/my-activity-submissions", label: "My & team's activity forms", icon: FileText, show: true, testid: "nav-my-activity-submissions", group: "FIELD_OPS" },
    { to: "/manager/retailer-list",       label: "Retailer list",         icon: Store,         show: true, testid: "nav-retailer-list",         group: "ORDERS_INVENTORY" },
    { to: "/distributor-stock",           label: "Distributor stock",     icon: Package,       show: true, testid: "nav-distributor-stock",     group: "ORDERS_INVENTORY" },
    { to: "/primary-order",               label: "Primary order",         icon: Truck,         show: hasWorkflowPerm("PRIMARY_ORDER"), testid: "nav-primary-order", group: "ORDERS_INVENTORY" },
    { to: "/manager/team-claims",         label: "Team claims dashboard", icon: BarChart3,     show: true, testid: "nav-team-claims",           group: "MONEY" },
    { to: "/queries",                     label: "Distributor Queries",   icon: MessagesSquare, show: LIMITED_MANAGER_ROLES.includes(user?.role_code), testid: "nav-field-distributor-queries", group: "ORDERS_INVENTORY" },
  ];
  const limited = LIMITED_MANAGER_ROLES.includes(user?.role_code);
  if (user?.role_code === "SALES_COORDINATOR") return items.filter((i) => i.show && COORDINATOR_NAV.has(i.to));
  return items.filter((i) => i.show && (limited ? LIMITED_MANAGER_NAV.has(i.to) : !LIMITED_MANAGER_ONLY.has(i.to)));
}

const GROUP_LABELS = [
  ["MASTERS", "Masters"],
  ["FIELD_OPS", "Field Ops"],
  ["ORDERS_INVENTORY", "Orders & Inventory"],
  ["MONEY", "Money"],
  ["REPORTS", "Reports"],
  ["SETTINGS", "Settings"],
];

function NavItem({ item, onClick }) {
  return (
    <NavLink
      to={item.to}
      end={item.exact}
      data-testid={item.testid}
      onClick={onClick}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
          isActive
            ? "tulsi-gradient text-white shadow-sm shadow-[#E5197F]/20"
            : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
        }`
      }
    >
      <item.icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function GroupSection({ groupKey, label, items, openMap, toggleGroup, onNavClick }) {
  const isOpen = openMap[groupKey] !== false; // default open
  return (
    <div className="mb-1" data-testid={`nav-group-${groupKey.toLowerCase()}`}>
      <button
        type="button"
        onClick={() => toggleGroup(groupKey)}
        data-testid={`nav-group-toggle-${groupKey.toLowerCase()}`}
        className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400 hover:text-neutral-600 transition-colors"
      >
        <span>{label}</span>
        <ChevronRight
          className={`h-3 w-3 transition-transform ${isOpen ? "rotate-90" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="space-y-0.5 mt-0.5">
          {items.map((item) => (
            <NavItem key={item.to} item={item} onClick={onNavClick} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function OfficeShell({ children }) {
  const { user, logout, hasPermission, hasAnyPermission, hasWorkflowPerm, canSwitchView, setViewMode } = useAuth();
  useFieldTracking(user);
  const navigate = useNavigate();
  const [openSide, setOpenSide] = useState(false);
  const [navQuery, setNavQuery] = useState("");
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem("tulsi_sidebar_groups");
      return raw ? JSON.parse(raw) : {};
    } catch (_) { return {}; }
  });
  const toggleGroup = (k) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [k]: prev[k] === false ? true : false };
      try { localStorage.setItem("tulsi_sidebar_groups", JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };

  const nav = useMemo(
    () => buildNav(user, hasPermission, hasAnyPermission, hasWorkflowPerm),
    [user, hasPermission, hasAnyPermission, hasWorkflowPerm]
  );
  const grouped = useMemo(() => {
    const map = {};
    for (const item of nav) {
      const key = item.group || "_ROOT";
      (map[key] = map[key] || []).push(item);
    }
    return map;
  }, [nav]);

  const filteredNav = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    if (!q) return null;
    return nav.filter((item) => item.label.toLowerCase().includes(q)
      || (item.keywords || []).some((k) => k.includes(q)));
  }, [nav, navQuery]);

  const { items: favorites } = useFavorites();

  const initials = (user?.name || "?")
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const closeSide = () => setOpenSide(false);

  const sidebarBody = (
    <>
      <div className="px-5 py-6 border-b border-neutral-100">
        <TulsiLogo size={40} />
      </div>
      <div className="px-3 pt-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
          <input
            type="text"
            value={navQuery}
            onChange={(e) => setNavQuery(e.target.value)}
            placeholder="Search menu..."
            data-testid="sidebar-search-input"
            className="w-full pl-8 pr-7 py-1.5 rounded-lg border border-neutral-200 bg-neutral-50 text-sm text-neutral-700 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#6D2C91]/20 focus:border-[#6D2C91]/40"
          />
          {navQuery && (
            <button
              type="button"
              onClick={() => setNavQuery("")}
              data-testid="sidebar-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <nav className="flex-1 px-3 py-3 overflow-y-auto">
        {filteredNav ? (
          <div className="space-y-0.5" data-testid="nav-search-results">
            {filteredNav.length ? (
              filteredNav.map((item) => (
                <NavItem key={item.to} item={item} onClick={closeSide} />
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-neutral-400">No matches</div>
            )}
          </div>
        ) : (
          <>
        {/* Dashboard pinned at top */}
        {(grouped._ROOT || []).map((item) => (
          <NavItem key={item.to} item={item} onClick={closeSide} />
        ))}

        {/* Favorites section (only when user has any) */}
        {favorites.length > 0 && (
          <div className="mt-2 mb-1" data-testid="nav-favorites">
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400 flex items-center gap-1">
              <Star className="h-3 w-3 fill-[#E5197F] text-[#E5197F]" /> Favorites
            </div>
            <div className="space-y-0.5">
              {favorites.map((f) => (
                <NavLink
                  key={f.item_key}
                  to={f.item_route}
                  data-testid={`nav-favorite-${f.item_key}`}
                  onClick={closeSide}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      isActive
                        ? "bg-[#6D2C91]/10 text-[#6D2C91]"
                        : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
                    }`
                  }
                >
                  <Star className="h-3 w-3 fill-[#E5197F] text-[#E5197F] shrink-0" />
                  <span className="truncate">{f.item_label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        )}

        {/* Grouped sections */}
        <div className="mt-2">
          {GROUP_LABELS.map(([key, label]) => {
            const rows = grouped[key] || [];
            if (!rows.length) return null;
            return (
              <GroupSection
                key={key}
                groupKey={key}
                label={label}
                items={rows}
                openMap={openGroups}
                toggleGroup={toggleGroup}
                onNavClick={closeSide}
              />
            );
          })}
        </div>
          </>
        )}
      </nav>
      <div className="px-5 py-4 border-t border-neutral-100 text-[11px] text-neutral-400">
        v0.26 · Phase 26A
      </div>
    </>
  );

  return (
    <div className="min-h-screen w-full flex bg-[#f7f5f9]" data-testid="office-shell">
      {/* Desktop sidebar — sticky + its own height so a long nav list
          scrolls independently of the main content (previously the aside
          had no height bound, so it grew to the full menu height, stretched
          the whole page, and scrolling either column moved both together —
          navigating to a low-down link then always "lost your place" back
          at the top of the sidebar). */}
      <aside className="hidden md:flex md:w-64 shrink-0 flex-col bg-white border-r border-neutral-200 md:h-screen md:sticky md:top-0">
        {sidebarBody}
      </aside>

      {/* Mobile sidebar drawer */}
      {openSide && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={closeSide}>
          <div className="absolute inset-0 bg-black/30" />
          <aside
            className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {sidebarBody}
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-neutral-200 flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-30">
          <button
            type="button"
            onClick={() => setOpenSide(true)}
            className="md:hidden h-9 w-9 flex items-center justify-center rounded-lg border border-neutral-200 text-neutral-600 hover:text-[#6D2C91] shrink-0"
            data-testid="office-sidebar-toggle"
            aria-label="Open menu"
          >
            <Menu className="h-4 w-4" />
          </button>

          <div className="hidden sm:block shrink-0">
            <div className="text-xs uppercase tracking-widest text-neutral-400">
              {user?.role_code?.replace(/_/g, " ")}
            </div>
            <div className="text-sm font-semibold text-neutral-800 truncate max-w-[220px]">
              {user?.name}
            </div>
          </div>

          <GlobalSearch />

          <div className="flex items-center gap-2 shrink-0">
            {canSwitchView && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setViewMode("field"); navigate("/"); }}
                data-testid="office-switch-to-field"
                className="h-9 px-2.5 gap-1.5 rounded-full border-[#6D2C91]/40 text-[#6D2C91] font-semibold"
                aria-label="Switch to Field app"
              >
                <Smartphone className="h-4 w-4" />
                <span>Field app</span>
              </Button>
            )}
            <SyncPill />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  data-testid="office-user-menu-trigger"
                  className="h-10 px-2 gap-2 rounded-full hover:bg-neutral-100"
                >
                  <div className="h-8 w-8 rounded-full tulsi-gradient text-white flex items-center justify-center text-xs font-bold">
                    {initials}
                  </div>
                  <ChevronDown className="h-4 w-4 text-neutral-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-neutral-500">
                  {user?.email || user?.phone}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate("/change-password")}
                  data-testid="office-menu-change-password"
                >
                  <KeyRound className="h-4 w-4 mr-2" /> Change password
                </DropdownMenuItem>
                {canSwitchView && (
                  <DropdownMenuItem
                    onClick={() => { setViewMode("field"); navigate("/"); }}
                    data-testid="office-menu-switch-field"
                  >
                    <Smartphone className="h-4 w-4 mr-2" /> Switch to Field app
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onLogout}
                  data-testid="office-menu-logout"
                  className="text-red-600 focus:text-red-700"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
      {/* unread admin broadcast messages — popup until "OK, I have read it" */}
      <BroadcastPopup />
      <LocationRequiredPopup user={user} />
    </div>
  );
}
